import { useMemo } from 'react';
import { RouteObject } from 'react-router-dom';

import { useFactorDetails, useGetUser } from 'app/api/hooks/core/v1/auth/user';
import { factorRoutes } from 'app/constants/routes';
import { useAuthState, useCanManageUsers } from 'app/state/AuthContext/hooks';
import { isFactorAdmin, isTriumphInternal } from 'app/utils/lib/user';

import {
  DefaultRedirect,
  FactorLayout,
  FourOhFour,
  Invoice,
  Invoices,
  InvoicesV2,
  Load,
  Loads,
  LoadsV2,
  Payment,
  Payments,
  Payors,
  Profile,
  Relationship,
  Relationships,
  UserCreate,
  UserEdit,
} from './FactorRouteComponents';

export const useGetFactorRoutes = (): RouteObject[] => {
  const { authRoles } = useAuthState();
  const user = useGetUser();
  const factorDetails = useFactorDetails();

  const canManageUsers = useCanManageUsers();

  const isAdmin = isFactorAdmin(user);
  const isInternal = isTriumphInternal(authRoles);

  return useMemo(() => [
    {
      children: [
        {
          element: <DefaultRedirect />,
          path: '',
        },
        {
          element: <Payors />,
          path: factorRoutes.payors,
        },
        {
          element: <Relationship />,
          path: `${factorRoutes.payeeRelationships}/:id`,
        },
        {
          element: <Relationships />,
          path: factorRoutes.payeeRelationships,
        },
        {
          element: <Load />,
          path: `${factorRoutes.loads}/:id`,
        },
        {
          element: factorDetails?.useAllLoadsSearch
            ? <LoadsV2 />
            : <Loads />,
          path: factorRoutes.loads,
        },
        {
          element: <Invoice />,
          path: `${factorRoutes.invoices}/:id`,
        },
        {
          element: factorDetails?.useAllInvoicesSearch
            ? <InvoicesV2 />
            : <Invoices />,
          path: factorRoutes.invoices,
        },
        {
          element: <Payment />,
          path: `${factorRoutes.payments}/:id`,
        },
        {
          element: <Payments />,
          path: factorRoutes.payments,
        },
        {
          element: <Profile />,
          path: factorRoutes.profile,
        },
        ...((isAdmin || isInternal) && canManageUsers
          ? [
            {
              element: <UserCreate />,
              path: `${factorRoutes.users}/new`,
            },
            {
              element: <UserEdit />,
              path: `${factorRoutes.users}/:id/edit`,
            },
          ]
          : []),
        {
          element: <FourOhFour />,
          path: '*',
        },
      ],
      element: <FactorLayout />,
      path: '/factor',
    },
  ],
  [canManageUsers, isAdmin, isInternal, factorDetails?.useAllInvoicesSearch, factorDetails?.useAllLoadsSearch]);
};
